import { useTranslation } from 'react-i18next';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';

export type LiveSectionPendingAction =
  | { kind: 'discard'; after: () => void }
  | { kind: 'switch'; target: string }
  | {
      kind: 'section-action';
      action: 'complete' | 'reopen';
      sectionId: string;
    };

type Props = {
  pending: LiveSectionPendingAction | null;
  sectionName?: string;
  busy: boolean;
  error: 'load' | 'save' | 'conflict' | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

export const LiveSectionConfirmation = ({
  pending,
  sectionName,
  busy,
  error,
  onClose,
  onConfirm
}: Props) => {
  const { t } = useTranslation();
  if (!pending) return null;
  const copy =
    pending.kind === 'discard'
      ? {
          title: t('live-discard-section-draft-title'),
          caption: t('live-discard-section-draft-copy'),
          confirm: t('live-discard-section-draft'),
          cancel: t('live-keep-editing')
        }
      : pending.kind === 'switch'
        ? {
            title: t('live-switch-section-title'),
            caption: t('live-switch-section-copy'),
            confirm: t('live-continue-viewing'),
            cancel: t('live-stay-here')
          }
        : {
            title: t(
              pending.action === 'complete'
                ? 'live-complete-section-title'
                : 'live-reopen-section-title',
              { name: sectionName }
            ),
            caption: t(
              pending.action === 'complete'
                ? 'live-complete-section-copy'
                : 'live-reopen-section-copy',
              { name: sectionName }
            ),
            confirm: t(
              pending.action === 'complete'
                ? 'live-complete-section'
                : 'live-reopen-section'
            ),
            cancel: t('cancel')
          };
  return (
    <ConfirmationModal
      isOpen
      onClose={onClose}
      onConfirm={onConfirm}
      title={copy.title}
      caption={copy.caption}
      confirmLabel={copy.confirm}
      cancelLabel={copy.cancel}
      confirmDisabled={busy}
      destructive={false}
    >
      {pending.kind === 'section-action' && error && (
        <p role="alert">{t(`live-section-${error}-error`)}</p>
      )}
    </ConfirmationModal>
  );
};
